export interface DraftJob {
  id: string;
  status: string;
  stage?: string | null;
  progress: { done: number; total: number };
  draft: { sectionId: string }[];
  writtenSlideIds: string[];
  audioSlides: { slideId: string; status: string }[];
  error?: string | null;
  createdAt?: unknown;
  usage?: {
    calls: number;
    costUsd: number;
    wastedCostUsd: number;
    slidesUsable: number;
    inputTokens: number;
    outputTokens: number;
  } | null;
}

const STATUS_LABEL: Record<string, string> = {
  pending: "Queued",
  running: "Generating",
  done: "Ready to review",
  failed: "Failed",
};

export function whenItRan(createdAt: unknown): string {
  if (typeof createdAt !== "string" && !(createdAt instanceof Date)) return "";
  return new Date(createdAt).toLocaleString();
}

/** A written job that has settled is history: no button acts on it, so the list drops it. */
export function isActionable(job: DraftJob): boolean {
  return job.writtenSlideIds.length === 0 || job.stage != null || job.error != null;
}

export function audioProgress(job: DraftJob): { done: number; total: number } | null {
  if (job.stage !== "audio" || job.audioSlides.length === 0) return null;
  return {
    done: job.audioSlides.filter((slide) => slide.status !== "pending").length,
    total: job.audioSlides.length,
  };
}

export function StatusPill({ job }: { job: DraftJob }) {
  const written = job.writtenSlideIds.length > 0;
  const label = audioProgress(job)
    ? "Adding audio"
    : written
      ? "Added to lesson"
      : (STATUS_LABEL[job.status] ?? job.status);
  const tone = written
    ? "bg-gray-500/10 text-gray-500"
    : job.status === "failed"
      ? "bg-error-500/10 text-error-500"
      : job.status === "done"
        ? "bg-accent-500/10 text-accent-500"
        : "bg-primary-500/10 text-primary-500";

  return <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${tone}`}>{label}</span>;
}
